import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import * as d3 from 'd3';
import { observer } from 'mobx-react-lite';
import { useFilterStore } from '/src/context';
import { Nullable } from '/src/lib/types';
import { colorPalette } from '/src/theme';
import { currency } from '/src/utils';

export type PieChartProps = {
  /**
   * Whether to display a `pie` or `donut` chart
   */
  chartType?: string;
  /**
   * Chart title
   */
  chartTitle?: string;
  /**
   * Data to display
   */
  chartData?: any[];
  /**
   * Text to show on chart hover
   */
  hoverText?: string;
  /**
   * Value to show on chart hover
   */
  hoverValue?: string | number;
  /**
   * Whether to show chart text
   */
  showText?: boolean;
  /**
   * Whether to show text/value on hover
   */
  showHover?: boolean;
  /**
   * Field to sort data by
   */
  sortBy?: string;
};

export const PieChart: React.FC<PieChartProps> = observer(
  ({
    chartType = 'donut',
    chartTitle = '',
    chartData: rawData,
    hoverText = '',
    hoverValue = '',
    showText = true,
    showHover = true,
    sortBy,
  }) => {
    /** Context **/
    const filterStore = useFilterStore();

    /** Refs **/
    // let element: Nullable<HTMLDivElement> = null;
    const element = useRef<Nullable<HTMLDivElement>>(null);

    /** State **/
    const [isReady, setIsReady] = useState(false);
    const [, setNoData] = useState<boolean>();

    /** Computed **/
    const chartData = useMemo(() => {
      let total = 0;
      const preparedData: any[] = [];

      // Calculate totals
      for (const item of rawData ?? []) {
        if (item[hoverValue] === 0) {
          setNoData(true);
        } else {
          // Add to total
          total += item[hoverValue];

          // Format for display
          item.display = {};
          item.display[hoverValue] = currency.format(item[hoverValue]);

          // Assign the given chart title
          item.chartTitle = chartTitle;

          // Finished preparing
          preparedData.push(item);
        }
      }

      // Sort data
      if (sortBy) preparedData.sort((a, b) => a[sortBy] - b[sortBy]);

      // Make chart draw clockwise
      preparedData.reverse();

      return { preparedData, total };
    }, [rawData, hoverValue, chartTitle]);

    const colorLibrary = useMemo(() => {
      const parser = d3.scaleOrdinal().range(colorPalette);
      const colorLibrary = [];

      for (const item of filterStore.companyListGet()) {
        colorLibrary.push({
          name: item.name,
          color: parser(item.name),
        });
      }

      return colorLibrary;
    }, [colorPalette, filterStore._companyList]);

    /** Methods **/

    /**
     * Gets an element's width
     */
    const getWidth = (element: Nullable<HTMLElement>) => {
      if (!element) return;

      if (isNaN(parseInt(d3.select(element).style('width'), 10))) return element.offsetWidth;
      else return parseInt(d3.select(element).style('width'), 10);
    };

    /**
     * Creates the pie chart SVG
     */
    const drawChart = (element: any, data: any) => {
      if (!element || !data) return;

      setIsReady(false);

      const drawLabel = (label = chartTitle, value: string | number = currency.format(data.total)) => {
        // if (!chart) return;

        // Remove any existing text or dividers
        chart.g.selectAll('text').remove();
        chart.g.selectAll('line').remove();

        // Draw text
        chart.g
          .append('text')
          .attr('transform', 'translate(0, -20)')
          .attr('dy', '0.35em')
          .style('text-anchor', 'middle')
          .attr('class', 'thin md-title capitalize')
          .text(label);

        // Draw divider
        chart.g
          .append('line')
          .attr('x1', '-60')
          .attr('x2', '60')
          .attr('y1', '0')
          .attr('y2', '0')
          .attr('stroke', 'black')
          .attr('stroke-width', '1');

        // Draw value
        chart.g
          .append('text')
          .attr('transform', 'translate(0, 20)')
          .attr('dy', '0.35em')
          .style('text-anchor', 'middle')
          .attr('class', 'md-title')
          .text(value);
      };

      // Create chart object
      const chart: Record<string, any> = {};

      // Define SVG dimensions
      chart.margins = {
        top: 30,
        right: 10,
        bottom: 30,
        left: 10,
      };
      chart.width = getWidth(element);
      chart.height = chart.width;
      chart.radius = Math.min(chart.width, chart.height) / 2;

      // Clear existing SVG
      d3.select(element).selectAll('svg').remove();

      // Create SVG
      chart.svg = d3
        .select(element)
        .append('svg')
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('viewBox', '0 0 ' + Math.min(chart.width, chart.height) + ' ' + Math.min(chart.width, chart.height))
        .attr('preserveAspectRatio', 'xMinYMin')
        // .attr('ng-if', isReady)
        .append('g')
        .attr('transform', 'translate(' + chart.width / 2 + ',' + chart.height / 2 + ')');

      // Create circle scale
      chart.arc = d3.arc().outerRadius(chart.radius - 10);
      if (chartType === 'donut') chart.arc.innerRadius(chart.radius - 50);
      else chart.arc.innerRadius(0);

      // Create the pie slice scale
      chart.pie = d3
        .pie()
        .sort(null)
        .value((d: any) => d[hoverValue]);

      // Draw a circle where the pie chart will be
      chart.g = chart.svg
        .selectAll('.arc')
        .data(() => chart.pie(data.preparedData))
        .enter()
        .append('g')
        .attr('class', 'arc');

      // Fill in the slices
      chart.g
        .append('path')
        .attr('d', (d: any) => {
          if (!d.endAngle) d.endAngle = 0;
          return chart.arc(d);
        })
        .style('fill', (d: any) => {
          for (const paletteItem of colorLibrary) {
            if (paletteItem.name === d.data[hoverText]) {
              return paletteItem.color;
            }
          }
        })
        .on('mouseover', (d: any) => showHover && drawLabel(d.data[hoverText], d.data.display[hoverValue]))
        .on('mouseleave', () => showText && drawLabel());

      drawLabel();

      setIsReady(true);
    };

    /** Effects **/

    useEffect(() => {
      return () => d3.select(element.current).selectAll('*').remove();
    }, []);

    useEffect(() => {
      if (element.current && chartData) drawChart(element.current, chartData);
    }, [element.current, chartData, hoverText, hoverValue]);

    /**** Render ****/

    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        {!isReady && <CircularProgress className="md-primary" />}
        <div
          ref={(node) => {
            element.current = node;
          }}
          id="chart-container"
          style={!isReady ? { display: 'none' } : undefined}
        />
      </Box>
    );
  }
);
